import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import NumericWidget from '@/features/dashboard/components/NumericWidget'
import type { SiteRevenuePerMarketProgram } from '@/features/site/types/revenue'
import { getCostsByMarketProgram } from '@/features/site/utils/revenueUtils/chartUtils'
import { formatCapacityMW, formatEnergy, formatRevenue } from '@/features/site/utils/revenueUtils/formatRevenueData'
import { getCurrencyFromRevenues } from '@/features/site/utils/revenueUtils/getCurrencyFromRevenues'
import { isFeeMarketProgram } from '@/features/site/utils/revenueUtils/isFeeMarketProgram'

type SiteRevenueWidgetsProps = {
  revenues: SiteRevenuePerMarketProgram | null
}

type RevenueMetrics = {
  netRevenue: number
  feeTotalsByProgram: Record<string, number>
  soldCapacity: number
  activatedEnergy: number
  hasFeeMarketPrograms: boolean
}

export function SiteRevenueWidgets({ revenues }: Readonly<SiteRevenueWidgetsProps>) {
  const { t } = useTranslation()

  const { netRevenue, feeTotalsByProgram, soldCapacity, activatedEnergy, hasFeeMarketPrograms } =
    calculateRevenueMetrics(revenues)
  // The costs definition is shared with the costs distribution chart (getCostsByMarketProgram):
  // only fee programs netting negative count, so the widget and the chart always show the same
  // numbers and appear together. A fee program netting zero or to a gain is not a cost.
  const overallCosts = Object.values(getCostsByMarketProgram(feeTotalsByProgram)).reduce(
    (total, cost) => total + cost,
    0,
  )
  const hasCosts = overallCosts > 0
  const currency = getCurrencyFromRevenues(revenues)
  // Customers without fee programs never see fees or the costs widget, so their tooltip
  // must not mention them (mirrors the dynamic market program filter label)
  const netRevenueTooltip = hasFeeMarketPrograms
    ? t('site_revenue.net_revenue_tooltip_with_fees')
    : t('site_revenue.net_revenue_tooltip')

  return (
    <Stack direction="row" gap={3} justifyContent="space-between">
      <NumericWidget
        disabled
        color={getRevenueColor(netRevenue)}
        title={t('site_revenue.net_revenue')}
        tooltip={netRevenueTooltip}
        value={formatRevenue(netRevenue, currency)}
        valueFontSize={24}
      />
      {hasCosts && (
        <NumericWidget
          disabled
          title={t('site_revenue.costs')}
          value={formatRevenue(overallCosts, currency)}
          valueFontSize={24}
        />
      )}
      <NumericWidget
        disabled
        title={t('site_revenue.sold_capacity')}
        value={formatCapacityMW(t, soldCapacity)}
        valueFontSize={24}
      />
      <NumericWidget
        disabled
        title={t('site_revenue.activated_energy')}
        value={formatEnergy(t, activatedEnergy)}
        valueFontSize={24}
      />
    </Stack>
  )
}

/**
 * Calculate the net revenue (all programs, fees included), the per-program fee totals, the
 * sold capacity, and the activated energy from the revenue data
 */
function calculateRevenueMetrics(revenues: SiteRevenuePerMarketProgram | null): RevenueMetrics {
  const metrics: RevenueMetrics = {
    netRevenue: 0,
    feeTotalsByProgram: {},
    soldCapacity: 0,
    activatedEnergy: 0,
    hasFeeMarketPrograms: false,
  }

  if (!revenues) {
    return metrics
  }

  for (const [marketProgram, revenueData] of revenues) {
    const isFee = isFeeMarketProgram(marketProgram)
    metrics.hasFeeMarketPrograms ||= isFee

    for (const row of revenueData ?? []) {
      metrics.netRevenue += row.revenue
      metrics.soldCapacity += row.soldCapacity
      metrics.activatedEnergy += row.activatedEnergy

      if (isFee) {
        metrics.feeTotalsByProgram[marketProgram] = (metrics.feeTotalsByProgram[marketProgram] ?? 0) + row.revenue
      }
    }
  }

  return metrics
}

/**
 * Returns the color of the revenue value based on its sign
 */
function getRevenueColor(revenue: number): 'positive' | 'negative' | 'neutral' {
  if (revenue > 0) return 'positive'
  if (revenue < 0) return 'negative'
  return 'neutral'
}
